import { useMemo } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { isSameComboboxOption } from '@/utils/comboboxOption';
import { matchesSearchText } from '@/utils/listSearch';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import {
  COMBOBOX_LIST_CLASS,
  COMBOBOX_PANEL_CLASS,
  DROPDOWN_ITEM_CLASS,
} from '@/styles/floatingPanel';
import { ActiveStatusBadge } from '../ActiveStatusBadge';
import { ComboboxSearchField } from '../ComboboxSearchField';
import { FIELD_LABEL_CLASS, FULL_ROW_FIELD_CLASS } from './formDialog.constants';

export interface SearchSelectOption {
  value: number;
  label: string;
  description?: string | undefined;
  searchText?: string | undefined;
  isActive?: boolean | undefined;
}

interface FormSearchSelectFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: Path<TValues>;
  label: string;
  options: readonly SearchSelectOption[];
  savedOption?: SearchSelectOption | undefined;
  onSearchChange?: ((search: string) => void) | undefined;
  isSearching?: boolean | undefined;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  isFullRow?: boolean | undefined;
}

function isSearchSelectOption(item: unknown): item is SearchSelectOption {
  return typeof item === 'object' && item !== null && 'label' in item && 'value' in item;
}

function matchesSearchSelectOption(item: unknown, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '' || !isSearchSelectOption(item)) return true;
  return [item.label, item.description ?? '', item.searchText ?? ''].some((text) =>
    matchesSearchText(text, needle),
  );
}

export function FormSearchSelectField<TValues extends FieldValues>({
  control,
  name,
  label,
  options,
  savedOption,
  onSearchChange,
  isSearching = false,
  placeholder,
  searchPlaceholder,
  emptyText,
  isFullRow = false,
}: FormSearchSelectFieldProps<TValues>) {
  const items = useMemo<SearchSelectOption[]>(() => {
    if (savedOption === undefined) return [...options];
    return [savedOption, ...options.filter((option) => option.value !== savedOption.value)];
  }, [options, savedOption]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(isFullRow && FULL_ROW_FIELD_CLASS)}>
          <FormLabel className={FIELD_LABEL_CLASS}>{label}</FormLabel>
          <Combobox<SearchSelectOption>
            items={items}
            value={items.find((item) => item.value === field.value) ?? null}
            onValueChange={(picked) => field.onChange(picked?.value)}
            isItemEqualToValue={isSameComboboxOption}
            filter={onSearchChange === undefined ? matchesSearchSelectOption : null}
            {...(onSearchChange === undefined ? {} : { onInputValueChange: onSearchChange })}
          >
            <FormControl>
              <ComboboxTrigger className={FIELD_CONTROL_CLASS}>
                <ComboboxValue placeholder={placeholder} />
                <ComboboxIcon />
              </ComboboxTrigger>
            </FormControl>
            <ComboboxContent className={COMBOBOX_PANEL_CLASS}>
              <ComboboxSearchField placeholder={searchPlaceholder} />
              <ComboboxList className={COMBOBOX_LIST_CLASS}>
                {(item: SearchSelectOption) => (
                  <ComboboxItem key={item.value} value={item} className={DROPDOWN_ITEM_CLASS}>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="font-medium">{item.label}</span>
                      {item.description ? (
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      ) : null}
                    </span>
                    {item.isActive === undefined ? null : (
                      <span className="ml-auto shrink-0 pl-3">
                        <ActiveStatusBadge isActive={item.isActive} />
                      </span>
                    )}
                  </ComboboxItem>
                )}
              </ComboboxList>
              <ComboboxEmpty>{isSearching ? LOADING_PLACEHOLDER : emptyText}</ComboboxEmpty>
            </ComboboxContent>
          </Combobox>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
